<template>
  <div class="content">

    <!-- Étape 1 : Type de commande -->
    <v-card outlined class="mb-4">
      <v-card-title style="font-size: 14px">Étape 1 : Type de commande</v-card-title>
      <v-card-text>
        <v-radio-group v-model="orderType" mandatory>
          <v-radio
            label="Bon de commande financé SEGUR Vague 2"
            value="withinSegur"
            color="#e2007a"
          ></v-radio>
          <v-radio
            v-if="isProkov"
            label="Pack SEGUR V1 gratuit"
            value="freeV1"
            color="#ff9800"
          ></v-radio>
          <v-radio
            label="Pack SEGUR V2 hors financement"
            value="outsideSegur"
            color="grey"
            disabled
          ></v-radio>
        </v-radio-group>
        <div v-if="isFreeV1" style="font-size: 12px; color: #ff9800; margin-top: -8px; padding-left: 32px">
          Pack V1 offert au médecin/MSP. Aucun PDF, aucune signature. Le BDC V2 du médecin sera ensuite facturé en V1V2.
        </div>
        <div v-else style="font-size: 12px; color: #999; margin-top: -8px; padding-left: 32px">
          Disponible à la fin de la campagne SEGUR Vague 2 (16 décembre 2026)
        </div>
      </v-card-text>
    </v-card>

    <!-- Étape 2 : Informations du praticien -->
    <v-card outlined class="mb-4">
      <v-card-title style="font-size: 14px">{{ step2Title }}</v-card-title>
      <v-card-text>
        <v-radio-group v-model="commandePour" row mandatory>
          <v-radio label="Individuel (libéral)" value="LIBERAL" color="#e2007a"></v-radio>
          <v-radio label="Structure (MSP)" value="STRUCTURE" color="#e2007a"></v-radio>
        </v-radio-group>

        <!-- RPPS + Licence (cachés en V1 gratuit MSP) -->
        <v-row v-if="!isFreeV1Structure" dense>
          <v-col v-if="rppsRequired" cols="12" sm="6">
            <v-text-field
              v-model="rpps"
              label="RPPS"
              maxlength="11"
              :loading="rppsLoading"
              :append-icon="rppsValidIcon"
              :rules="[rules.rppsFormat]"
              @input="onRppsInput"
              outlined
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <div style="display: flex; align-items: flex-start; gap: 8px">
              <v-select
                v-if="!manualLicence"
                v-model="selectedLicence"
                :items="licences"
                item-text="display"
                item-value="licenceID"
                :label="licenceLabel"
                :disabled="!rppsValid"
                :loading="rppsLoading"
                outlined
                dense
                style="flex: 1"
              ></v-select>
              <div v-if="manualLicence" style="flex: 1">
                <v-text-field
                  v-model="manualLicenceId"
                  :label="licenceLabel + ' (saisie manuelle)'"
                  :disabled="!rppsValid"
                  :append-icon="manualLicenceValidIcon"
                  @input="checkManualLicence"
                  outlined
                  dense
                ></v-text-field>
                <v-chip
                  v-if="manualLicenceValid === false"
                  small
                  color="orange"
                  outlined
                  style="margin-top: -16px; margin-bottom: 8px"
                >
                  <v-icon small left>mdi-information-outline</v-icon>
                  Cette licence n'existe pas dans la base - Elle sera utilisée telle quelle
                </v-chip>
              </div>
              <v-btn
                icon
                small
                color="#e2007a"
                @click="manualLicence = !manualLicence"
                :disabled="!rppsValid"
                :title="manualLicence ? 'Revenir à la sélection' : 'Saisie manuelle'"
                style="margin-top: 4px"
              >
                <v-icon small>{{ manualLicence ? 'mdi-close' : 'mdi-pencil' }}</v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <!-- Genre + Nom + Prénom (gérant pour MSP V1 gratuit) -->
        <v-row dense>
          <v-col cols="12" sm="3">
            <v-select
              v-model="genre"
              :items="[{ text: 'Madame', value: 'Madame' }, { text: 'Monsieur', value: 'Monsieur' }]"
              label="Genre"
              outlined
              dense
            ></v-select>
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field v-model="nom" label="Nom" outlined dense></v-text-field>
          </v-col>
          <v-col cols="12" sm="5">
            <v-text-field v-model="prenom" label="Prénom" outlined dense></v-text-field>
          </v-col>
        </v-row>

        <!-- Téléphone + Email (gérant pour MSP V1 gratuit) -->
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field v-model="telephone" label="Téléphone" outlined dense></v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field v-model="email" label="Email" type="email" outlined dense></v-text-field>
          </v-col>
        </v-row>

        <!-- Adresse + CP + Ville (gérant pour MSP V1 gratuit) -->
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field v-model="adresse" label="Adresse" outlined dense></v-text-field>
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field v-model="codePostal" label="Code postal" maxlength="5" outlined dense></v-text-field>
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field v-model="ville" label="Ville" outlined dense></v-text-field>
          </v-col>
        </v-row>

        <!-- Structure : FINESS juridique + FINESSGEO etablissement + Raison sociale + Effectif + Tranche -->
        <template v-if="commandePour === 'STRUCTURE'">
          <v-divider class="my-3"></v-divider>
          <v-row>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="finess"
                label="FINESS juridique"
                maxlength="9"
                :loading="finessLoading"
                :rules="finessFieldRules"
                hint="9 chiffres - FINESS de l'entite juridique"
                persistent-hint
                outlined
                dense
              ></v-text-field>
              <div v-if="finessCompletionSuggestion" class="mt-1">
                <v-btn small text color="primary" @click="adoptFinessCompletion">
                  Utiliser le FINESS complete : {{ finessCompletionSuggestion }}
                </v-btn>
              </div>
            </v-col>
            <v-col cols="12" sm="4" v-if="showFinessGeo && !isFreeV1Structure">
              <!-- FINESSGEO : visible pour SaaS, masque Prokov si MSPList a fourni la valeur -->
              <v-text-field
                v-model="finessGeo"
                label="FINESS geographique (etablissement)"
                maxlength="9"
                :readonly="isAnsResolved"
                :rules="[v => !v || /^(2[AB]\d{7}|\d{9})$/.test(v) || 'FINESSGEO : 9 chiffres ou un FINESS corse 2A/2B']"
                :hint="isAnsResolved ? 'Site resolu via le referentiel ANS' : '9 chiffres - sur le contrat ANS, ligne \'etablissement\' (different du FINESS juridique)'"
                persistent-hint
                outlined
                dense
                required
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4" v-if="!isFreeV1Structure">
              <v-text-field
                v-model.number="effectif"
                label="Effectif"
                type="number"
                min="1"
                :readonly="mspFound"
                :hint="!mspFound && mspChecked ? 'Optionnel : pour information uniquement' : ''"
                persistent-hint
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12" sm="8">
              <!-- raisonSociale editable (meme si MSPList l'a fourni, peut etre corrigee) -->
              <v-text-field
                v-model="mspRaisonSociale"
                label="Raison sociale de la structure"
                :hint="mspFound && mspRaisonSocialeFromLookup ? 'Valeur Base ANS (modifiable)' : 'Saisie manuelle'"
                persistent-hint
                outlined
                dense
                required
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4" v-if="!isFreeV1Structure">
              <!-- Tranche en lecture seule si fournie par MSPList ou forcee par l'ANS,
                   sinon selecteur manuel obligatoire. -->
              <v-text-field
                v-if="trancheLockedByLookup"
                v-model="tranche"
                label="Tranche"
                readonly
                :hint="isAnsResolved ? 'Forcee par le referentiel ANS' : 'Valeur Base ANS'"
                persistent-hint
                outlined
                dense
              ></v-text-field>
              <v-select
                v-else
                v-model="tranche"
                :items="['A', 'B', 'C', 'D', 'E']"
                label="Tranche (Base ANS)"
                hint="À demander à la structure ou à consulter sur la Base de référence ANS"
                persistent-hint
                outlined
                dense
              ></v-select>
            </v-col>
          </v-row>
          <v-row dense v-if="!isFreeV1Structure && isPartnerRefEnabled">
            <v-col cols="12" sm="8">
              <v-text-field
                v-model="partnerRef"
                label="Référence interne (partenaire)"
                maxlength="64"
                hint="Optionnel : identifiant interne partenaire, non transmis à l'ASP"
                persistent-hint
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>
          <v-chip
            v-if="finessFormatInvalid"
            small
            color="error"
            outlined
            class="mt-1"
          >
            <v-icon small left>mdi-alert-circle-outline</v-icon>
            FINESS invalide : 9 chiffres requis.
          </v-chip>
          <v-chip
            v-else-if="mspFound"
            small
            color="success"
            outlined
            class="mt-1"
          >
            <v-icon small left>mdi-check-circle</v-icon>
            Trouvé via Base ANS<span v-if="mspStructureTypeLabel"> ({{ mspStructureTypeLabel }})</span> :
            {{ mspRaisonSociale || '(raison sociale inconnue)' }}
          </v-chip>
          <v-chip
            v-else-if="mspChecked && !isAnsActive"
            small
            color="orange"
            outlined
            class="mt-1"
          >
            <v-icon small left>mdi-pencil-outline</v-icon>
            {{ mspManualEntryMessage }}
          </v-chip>

          <!-- Statut referentiel ANS (flag actif uniquement) -->
          <v-chip
            v-if="isAnsResolved"
            small
            color="primary"
            outlined
            class="mt-1 ml-1"
          >
            <v-icon small left>mdi-shield-check</v-icon>
            Tranche {{ tranche }} forcée par le référentiel ANS
          </v-chip>
          <v-alert
            v-if="isAnsAbsent"
            type="warning"
            outlined
            dense
            class="mt-2 mb-0"
          >
            Structure absente du référentiel ANS Va2 : bon de commande structure
            impossible. Créer un bon de commande libéral à la place.
          </v-alert>
          <v-alert
            v-else-if="ansMode === 'error'"
            type="error"
            outlined
            dense
            class="mt-2 mb-0"
          >
            Référentiel ANS momentanément indisponible. Réessayez en re-saisissant le FINESS.
          </v-alert>
          <v-alert
            v-else-if="ansMode === 'ambiguous'"
            type="info"
            outlined
            dense
            class="mt-2 mb-0"
          >
            Ce FINESS juridique couvre plusieurs sites.
            <v-btn small text color="primary" class="ml-2" @click="ansDialog = true">
              Choisir le site
            </v-btn>
          </v-alert>
        </template>
      </v-card-text>
    </v-card>

    <!-- Choix du site pour un FINESS juridique multi-sites (referentiel ANS) -->
    <v-dialog v-model="ansDialog" max-width="640" persistent>
      <v-card>
        <v-card-title style="font-size: 15px">
          Selection du site (FINESS geographique)
        </v-card-title>
        <v-card-text>
          <div style="font-size: 13px; color: #555; margin-bottom: 12px">
            Ce FINESS juridique regroupe plusieurs etablissements. Chaque site a sa
            propre tranche : selectionnez celui correspondant au bon de commande.
          </div>
          <v-list two-line>
            <v-list-item
              v-for="site in ansSites"
              :key="site.FINESSGEO"
              @click="selectAnsSite(site)"
            >
              <v-list-item-content>
                <v-list-item-title>
                  {{ site.raisonSociale || '(raison sociale inconnue)' }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  FINESSGEO {{ site.FINESSGEO }} - Tranche {{ site.tranche }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon color="primary">mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="ansDialog = false">Annuler</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Étape 3 : Récapitulatif et montants (V2) ou Création directe (V1 gratuit) -->
    <v-card outlined class="mb-4">
      <v-card-title style="font-size: 14px">{{ isFreeV1 ? 'Étape 3 : Création du pack V1 gratuit' : 'Étape 3 : Récapitulatif et montants' }}</v-card-title>
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" sm="4">
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dateSignature"
                  label="Date de signature"
                  prepend-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="dateSignature"
                no-title
                @input="dateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col v-if="!isFreeV1" cols="12" sm="8">
            <div style="display: flex; align-items: center; gap: 12px">
              <v-select
                v-model="typePrestation"
                :items="typePrestationItems"
                label="Type de prestation"
                outlined
                dense
                :disabled="!rppsValid || existingBDC != null"
                @change="computePricingPreview"
                style="flex: 1; max-width: 320px"
              ></v-select>
              <v-chip
                v-if="typePrestation"
                :color="typePrestation === 'V2' ? '#e2007a' : '#ff9800'"
                text-color="white"
                small
                style="margin-bottom: 22px"
              >
                {{ typePrestation === 'V2' ? 'Déjà équipé V1' : 'Jamais équipé' }}
              </v-chip>
              <span v-else style="font-size: 12px; color: #999; margin-bottom: 22px">
                Auto-détecté via le RPPS
              </span>
            </div>
          </v-col>
        </v-row>

        <!-- Mini-cards montants (cachées en V1 gratuit : pas de tarification) -->
        <v-row v-if="pricing && !isFreeV1" dense class="mt-2">
          <v-col cols="12" sm="4" class="d-flex">
            <v-card outlined style="text-align: center; padding: 16px; width: 100%">
              <div style="font-size: 12px; color: #888; text-transform: uppercase; letter-spacing: 0.5px">Montant HT</div>
              <div style="font-size: 28px; font-weight: bold; color: #e2007a; margin-top: 8px">{{ formatEur(pricing.montantHT) }}</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="4" class="d-flex">
            <v-card outlined style="text-align: center; padding: 16px; width: 100%">
              <div style="font-size: 12px; color: #888; text-transform: uppercase; letter-spacing: 0.5px">TVA {{ pricing.tauxTVA }}%</div>
              <div style="font-size: 28px; font-weight: bold; color: #333; margin-top: 8px">{{ formatEur(pricing.montantTTC) }}</div>
              <div style="font-size: 11px; color: #888">TTC</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="4" class="d-flex">
            <v-card outlined style="text-align: center; padding: 16px; width: 100%">
              <div style="font-size: 12px; color: #888; text-transform: uppercase; letter-spacing: 0.5px">Avance / Solde</div>
              <div style="font-size: 18px; font-weight: bold; color: #4caf50; margin-top: 8px">40% : {{ formatEur(pricing.avanceTTC) }}</div>
              <div style="font-size: 14px; color: #ff9800; margin-top: 2px">60% : {{ formatEur(pricing.soldeTTC) }}</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Aides de minimis : obligatoire pour les BDC libéraux financés. -->
        <div v-if="showAidesMinimis" class="aides-minimis-panel mt-4">
          <div style="font-size: 13px; font-weight: 600; margin-bottom: 8px">Aides de minimis</div>
          <div style="font-size: 12px; color: #555; margin-bottom: 10px">
            Aides minimis SIEG perçues hors financement Ségur dans les 3 ans précédant la signature.
          </div>
          <v-radio-group v-model="aidesPercues" row mandatory dense class="mt-0">
            <v-radio label="Non" :value="false" color="#e2007a"></v-radio>
            <v-radio label="Oui" :value="true" color="#e2007a"></v-radio>
          </v-radio-group>
          <aides-de-minimis-editor
            v-if="aidesPercues === true"
            v-model="aidesDeMinimis"
          ></aides-de-minimis-editor>
          <v-alert
            v-if="aidesMinimisError"
            type="error"
            outlined
            dense
            class="mt-3 mb-0"
          >
            {{ aidesMinimisError }}
          </v-alert>
        </div>

        <!-- BDC V2 existant : sans objet pour un pack V1 gratuit, qui n'a ni PDF ni étape 4. -->
        <v-alert v-if="!isFreeV1 && existingBDC && !existingBDC.hasPdf" type="warning" outlined class="mt-4">
          Un BDC V2 existe déjà pour {{ commandePour === 'STRUCTURE' ? 'cette structure' : 'ce praticien' }} (n° {{ existingBDC.numeroEditeur }}).
          Rendez-vous à l'étape 4 ci-dessous pour le faire signer.
        </v-alert>

        <!-- BDC existant avec PDF : tout est complet -->
        <v-alert v-else-if="!isFreeV1 && existingBDC && existingBDC.hasPdf" type="success" outlined class="mt-4">
          {{ commandePour === 'STRUCTURE' ? 'Cette structure a' : 'Ce praticien a' }} déjà un BDC V2 complet (n° {{ existingBDC.numeroEditeur }}, PDF uploadé).
        </v-alert>

        <!-- Alert backend 400 types (persistant, pas snackbar) -->
        <v-alert
          v-if="backendError"
          type="error"
          outlined
          dismissible
          class="mt-4"
          @input="backendError = null"
        >
          <strong>[{{ backendError.code }}]</strong> {{ backendError.msg }}
        </v-alert>

        <v-alert
          v-if="orderFormId && numeroEditeur && !existingBDC && !isFreeV1"
          type="success"
          outlined
          class="mt-4"
        >
          BDC brouillon créé (n° {{ numeroEditeur }}). Téléchargez le PDF brouillon à l'étape 4, faites-le signer, puis uploadez le PDF signé.
        </v-alert>

        <!-- Pas de BDC : bouton création avec tooltip champs manquants. Un BDC V2 ne
             bloque pas le pack V1 gratuit : les deux coexistent, le V2 étant alors
             facturé en V1V2. -->
        <div
          v-if="(isFreeV1 || !existingBDC) && !(orderFormId && numeroEditeur && !isFreeV1)"
          class="mt-4"
        >
          <v-alert
            v-if="createBlockedReason"
            type="error"
            outlined
            dense
            class="mb-3"
          >
            {{ createBlockedReason }}
          </v-alert>
          <v-tooltip bottom :disabled="canCreatePack">
            <template #activator="{ on }">
              <div v-on="on" style="display: inline-block">
                <v-btn
                  :color="isFreeV1 ? '#ff9800' : '#e2007a'"
                  dark
                  large
                  :loading="creating"
                  :disabled="!canCreatePack"
                  @click="createBDC"
                >
                  {{ isFreeV1 ? 'Créer le pack V1 gratuit' : 'Créer le bon de commande' }}
                </v-btn>
              </div>
            </template>
            <span v-if="createBlockedReason">{{ createBlockedReason }}</span>
          </v-tooltip>
        </div>
      </v-card-text>
    </v-card>

    <!-- Étape 4 : Signature du BDC (électronique à distance ou scan papier). V2 uniquement. -->
    <v-card v-if="numeroEditeur && !isFreeV1" ref="uploadStep" outlined class="mb-4">
      <v-card-title style="font-size: 14px">Étape 4 : Signature du bon de commande</v-card-title>
      <v-card-text>
        <v-alert v-if="!isAwaitingFirstSign" type="info" outlined class="mb-4">
          Le numéro éditeur <strong>{{ numeroEditeur }}</strong> a été généré. Envoyez le BDC au
          médecin pour signature électronique, ou faites-le signer sur le PDF brouillon puis
          uploadez le scan.
        </v-alert>

        <v-text-field
          :value="numeroEditeur"
          label="Numero editeur"
          readonly
          outlined
          dense
          append-icon="mdi-content-copy"
          @click:append="copyNumeroEditeur"
        ></v-text-field>

        <!-- BDC brouillon à faire signer hors du logiciel : scan papier ou prestataire
             de signature électronique. -->
        <template v-if="canDownloadDraft">
          <div style="font-size: 13px; color: #555">Mention portée sur le BDC brouillon</div>
          <v-radio-group
            v-model="draftSignatureType"
            :disabled="downloadingDraft"
            row
            dense
            class="mt-1"
          >
            <v-radio label="Signature manuscrite" value="manuscript" color="#e2007a"></v-radio>
            <v-radio label="Signature électronique" value="electronic" color="#e2007a"></v-radio>
          </v-radio-group>

          <v-btn
            color="#1976d2"
            outlined
            :loading="downloadingDraft"
            :disabled="downloadingDraft"
            @click="downloadDraftPdf"
            class="mb-3"
            block
          >
            <v-icon left>mdi-file-download-outline</v-icon>
            Télécharger le BDC brouillon à faire signer
          </v-btn>
        </template>

        <!-- Signature électronique à distance : le médecin lit et signe sans uploader de scan. -->
        <v-btn
          v-if="canSendForSign"
          color="#e2007a"
          outlined
          :loading="sendingForSign"
          :disabled="sendingForSign"
          @click="sendForSign"
          class="mb-3"
          block
        >
          <v-icon left>mdi-email-send-outline</v-icon>
          Envoyer au médecin pour signature électronique
        </v-btn>

        <v-alert v-if="isAwaitingFirstSign" type="info" outlined dense class="mb-3">
          <div class="d-flex align-center" style="flex-wrap: wrap; gap: 8px">
            <span>
              <v-icon small left>mdi-clock-alert-outline</v-icon>
              En attente de signature du médecin<template v-if="sentForSignAtDisplay"> (envoyé le {{ formatSentForSign(sentForSignAtDisplay) }})</template>.
            </span>
            <v-spacer></v-spacer>
            <v-btn
              x-small
              text
              color="error"
              :loading="sendingForSign"
              :disabled="sendingForSign"
              @click="cancelSendForSign"
            >
              <v-icon x-small left>mdi-close-circle-outline</v-icon>
              Annuler l'envoi
            </v-btn>
          </div>
        </v-alert>

        <!-- Scan papier : masqué pendant l'attente d'une signature électronique (le serveur le refuse). -->
        <template v-if="!isAwaitingFirstSign">
          <v-file-input
            v-model="pdfFile"
            label="PDF du BDC signe"
            accept="application/pdf"
            :rules="[rules.pdfSize]"
            outlined
            dense
            prepend-icon="mdi-file-pdf-box"
            class="mt-2"
          ></v-file-input>

          <v-btn
            color="#e2007a"
            dark
            :loading="uploading"
            :disabled="!pdfFile || uploading"
            @click="sendPdf"
            class="mt-2"
          >
            Envoyer le PDF
          </v-btn>
        </template>
      </v-card-text>
    </v-card>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Fermer</v-btn>
      </template>
    </v-snackbar>

  </div>
</template>

<script>
import axios from "axios"
import store from "@/store"
import server from "@/store/server"
import AidesDeMinimisEditor from "@/components/Segur/AidesDeMinimisEditor.vue"

const API_BASE = server.url

/**
 * Grille tarifaire SEGUR Vague 2.
 * [SEGUR-SPEC] Montants imposes par AF-MDV-LGC-Va2 Annexe 3.
 */
const MONTANTS = {
  V2: { LIBERAL: 376.25, STRUCTURE: { A: 2633.75, B: 4515, C: 6396.25, D: 8277.50, E: 10158.75 } },
  V1V2: { LIBERAL: 483.75, STRUCTURE: { A: 3386.25, B: 5805, C: 8223.75, D: 10642.50, E: 13061.25 } }
}

/**
 * Calcul du taux de TVA selon le code postal (departements d'outre-mer).
 * [SEGUR-SPEC] Guyane/Saint-Pierre/Mayotte = 0%, Guadeloupe/Martinique/Reunion = 8.5%, reste = 20%.
 */
function getTauxTVA(cp) {
  if (!cp || cp.length < 3) return 20
  const p = cp.substring(0, 3)
  if (p === '973' || p === '975' || p === '976') return 0
  if (p === '971' || p === '972' || p === '974') return 8.5
  return 20
}

function round2(val) {
  return Math.round((val + Number.EPSILON) * 100) / 100
}

export default {
  name: "SegurBon",
  components: {
    AidesDeMinimisEditor
  },
  data() {
    return {
      orderType: 'withinSegur',
      commandePour: 'LIBERAL',
      rpps: '',
      rppsValid: null,
      rppsLoading: false,
      licences: [],
      selectedLicence: null,
      manualLicence: false,
      manualLicenceId: '',
      manualLicenceValid: null,
      genre: 'Madame',
      nom: '',
      prenom: '',
      telephone: '',
      email: '',
      adresse: '',
      codePostal: '',
      ville: '',
      finess: '',
      finessGeo: '',
      // FINESS complété (clé Luhn reconstituée par le serveur) proposé quand la saisie fait
      // 8 chiffres. Adopté seulement sur clic, jamais substitué automatiquement.
      finessCompletionSuggestion: null,
      effectif: null,
      tranche: null,
      // Identifiant interne partenaire (optionnel, descriptif, jamais transmis à l'ASP).
      partnerRef: '',
      finessLoading: false,
      mspChecked: false,
      mspFound: false,
      // Occupation du site retenu, vérifiée par FINESSGEO :
      // idle | loading | free | taken | ambiguous | error. Fail-closed : ambiguous et
      // error bloquent la création, que le serveur refuserait de toute façon.
      siteOccupancy: 'idle',
      mspRaisonSociale: '',
      mspRaisonSocialeFromLookup: false,
      mspCategorieFiness: null,
      mspLibCategorieFiness: '',
      // Référentiel ANS : pré-résolution de la tranche + choix de site multi-sites.
      // ansMode : 'disabled' (flag off) | 'resolved' | 'ambiguous' | 'absent' | 'error' | null.
      ansMode: null,
      ansSites: [],
      ansDialog: false,
      ansLoading: false,
      backendError: null,
      dateMenu: false,
      dateSignature: new Date().toISOString().substr(0, 10),
      typePrestation: null,
      pricing: null,
      aidesPercues: false,
      aidesDeMinimis: [],
      existingBDC: null,
      // 'FINESS' (STRUCTURE) ou 'RPPS' (LIBERAL) : évite qu'une détection RPPS sans match
      // efface un BDC trouvé via FINESS.
      existingBdcSource: null,
      numeroEditeur: null,
      orderFormId: null,
      pdfFile: null,
      // Mention imprimée sous le bloc signature du brouillon : 'manuscript' ou 'electronic'.
      draftSignatureType: 'manuscript',
      creating: false,
      uploading: false,
      downloadingDraft: false,
      sendingForSign: false,
      // Première signature à distance : id du BDC envoyé localement (état transitoire avant
      // rechargement), lié à orderFormId pour ne pas polluer un autre BDC repris ensuite.
      // Au rechargement, la vérité vient de existingBDC.awaitingFirstSign.
      localAwaitingOrderFormId: null,
      localSentForSignAt: null,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      residentId: null,
      rules: {
        rppsFormat: v => !v || /^\d{0,11}$/.test(v) || 'Le RPPS doit contenir 11 chiffres',
        finessFormat: v => !v || /^(2[AB]\d{7}|\d{9})$/.test(v) || 'Le FINESS doit contenir 9 chiffres ou un FINESS corse 2A/2B',
        pdfSize: v => !v || v.size <= 3 * 1024 * 1024 || 'Le fichier ne doit pas depasser 3 Mo'
      }
    }
  },
  created() {
    // Tokens non-reactifs (pas de re-render, usage interne pour race control)
    this._finessLookupToken = 0
    this._rppsLookupToken = 0
  },
  computed: {
    // Brouillon dispo si BDC fraîchement créé (existingBDC null) ou existant en state=OFF sans PDF.
    canDownloadDraft() {
      if (!this.orderFormId) return false
      if (!this.existingBDC) return true
      return this.existingBDC.state === 'OFF' && !this.existingBDC.hasPdf
    },
    isAwaitingFirstSign() {
      const localMatch = this.localAwaitingOrderFormId !== null && this.localAwaitingOrderFormId === this.orderFormId
      return localMatch || this.existingBDC?.awaitingFirstSign === true
    },
    sentForSignAtDisplay() {
      const localMatch = this.localAwaitingOrderFormId !== null && this.localAwaitingOrderFormId === this.orderFormId
      return (localMatch ? this.localSentForSignAt : null) || this.existingBDC?.sentForSignAt || null
    },
    // Envoi possible pour un BDC V2 brouillon (OFF, sans PDF) pas encore en attente. STRUCTURE
    // sans tranche et wave V1 sont refusés côté serveur avec un message explicite.
    canSendForSign() {
      if (this.isFreeV1 || !this.orderFormId) return false
      if (this.isAwaitingFirstSign) return false
      if (this.existingBDC) return this.existingBDC.state === 'OFF' && !this.existingBDC.hasPdf
      return true
    },
    rppsValidIcon() {
      if (this.rppsLoading) return ''
      if (this.rppsValid === true) return 'mdi-check-circle'
      if (this.rppsValid === false) return 'mdi-close-circle'
      return ''
    },
    manualLicenceValidIcon() {
      if (this.manualLicenceValid === true) return 'mdi-check-circle'
      if (this.manualLicenceValid === false) return 'mdi-close-circle'
      return ''
    },
    typePrestationItems() {
      return [
        { text: 'V2 (déjà équipé V1)', value: 'V2' },
        { text: 'V1V2 (jamais équipé)', value: 'V1V2' },
      ]
    },
    // Pack V1 gratuit : Prokov uniquement. Fail-closed : pas de fallback si partnerCode absent.
    isProkov() {
      return (localStorage.getItem('partnerCode') || '').toLowerCase() === 'prokov'
    },
    isFreeV1() {
      return this.orderType === 'freeV1'
    },
    // V1 gratuit MSP : workflow allégé sans RPPS, licence, identité médecin, adresse, tranche.
    isFreeV1Structure() {
      return this.isFreeV1 && this.commandePour === 'STRUCTURE'
    },
    rppsRequired() {
      return !this.isFreeV1Structure && !this.isStructureWithoutRpps
    },
    finessFormatInvalid() {
      // Une saisie à 8 chiffres n'est pas invalide mais incomplète : tant qu'une
      // proposition de complétion existe, le bouton bleu guide, pas l'erreur rouge.
      if (this.finessCompletionSuggestion) return false
      return !!this.finess && !/^(2[AB]\d{7}|\d{9})$/.test(this.finess)
    },
    // Règles du champ FINESS : neutralisées pendant qu'une proposition de complétion
    // existe, pour ne pas afficher une erreur rouge contradictoire avec le bouton.
    finessFieldRules() {
      return this.finessCompletionSuggestion ? [] : [this.rules.finessFormat]
    },
    mspManualEntryMessage() {
      const typeLabel = this.mspStructureTypeLabel
      if (typeLabel) {
        return `FINESS trouvé en Base ANS (${typeLabel}), mais effectif/tranche manquant : saisie manuelle requise.`
      }
      if (this.isFreeV1Structure) {
        return 'FINESS absent de la Base ANS structure : saisissez la raison sociale manuellement.'
      }
      return 'FINESS inconnu en Base ANS structure : saisie manuelle requise (FINESSGEO + raison sociale + tranche).'
    },
    mspStructureTypeLabel() {
      const code = String(this.mspCategorieFiness || '')
      const labels = {
        '603': 'MSP',
        '124': 'centre de santé',
        '228': 'centre de santé sexuelle',
        '649': 'centre de santé et médiation en santé sexuelle'
      }
      if (labels[code]) return labels[code]
      return this.mspLibCategorieFiness || ''
    },
    canCreatePack() {
      if (this.creating) return false
      if (this.rppsRequired && !this.rppsValid) return false
      if (this.responsableMissingFields.length) return false
      if (this.codePostalMissing) return false
      if (this.aidesMinimisError) return false
      return this.structureComplete
    },
    createBlockedReason() {
      if (this.canCreatePack) return null
      if (this.rppsRequired && !this.rppsValid) return 'RPPS invalide ou manquant'
      if (this.responsableMissingFields.length) {
        return 'Responsable structure : ' + this.responsableMissingFields.join(', ')
      }
      if (this.codePostalMissing) return 'Code postal obligatoire (5 chiffres)'
      if (this.structureMissingFields.length) {
        return 'Champs structure manquants : ' + this.structureMissingFields.join(', ')
      }
      if (this.aidesMinimisError) return this.aidesMinimisError
      return null
    },
    showAidesMinimis() {
      return !this.isFreeV1 && this.commandePour === 'LIBERAL'
    },
    aidesMinimisError() {
      if (!this.showAidesMinimis) return null
      if (this.aidesPercues === false) return null
      if (this.aidesPercues !== true) return 'Déclaration des aides de minimis requise'

      const aides = Array.isArray(this.aidesDeMinimis) ? this.aidesDeMinimis : []
      if (aides.length === 0) {
        return 'Ajoutez au moins une aide de minimis ou sélectionnez Non'
      }

      const invalid = aides.some((aide) => {
        const date = String(aide?.dateAttribution || '')
        const montant = Number(aide?.montantAides)
        return !/^\d{2}\/\d{2}\/\d{4}$/.test(date) || !(montant > 0)
      })
      return invalid ? 'Chaque aide doit avoir une date JJ/MM/AAAA et un montant > 0' : null
    },
    // Libelle du champ licence par partenaire (Licence MS4, Licence Easy Care, etc.).
    licenceLabel() {
      return localStorage.getItem('partnerLicenceLabel') || 'Licence'
    },
    // Flags métier du partenaire, rafraîchis par le heartbeat via /tokenIsValid.
    // Lecture du getter Vuex et non du localStorage : ce dernier n'étant pas réactif,
    // un computed qui le lit serait mis en cache et ne verrait jamais le changement.
    partnerFeatures() {
      return this.$store.getters['authentification/getPartnerFeatures']
    },
    // Structure sans RPPS : le responsable est identifié par état civil + email.
    isStructureWithoutRpps() {
      return this.commandePour === 'STRUCTURE' && this.partnerFeatures.structureWithoutRpps === true
    },
    // Identité structure par site. Reflet d'une décision serveur : le front n'active
    // rien, il évite d'afficher un pré-check que le serveur refuserait.
    isSiteIdentityEnabled() {
      return this.partnerFeatures.adminStructureSiteIdentityEnabled === true
    },
    // Identifiant interne partenaire (ICT) : champ affiché uniquement sous ce flag.
    isPartnerRefEnabled() {
      return this.partnerFeatures.partnerRefEnabled === true
    },
    // Un BDC repris depuis l'identité structure (FINESS saisi ou site résolu) ne doit pas
    // être effacé par un lookup RPPS vide ou en erreur : c'est la structure qui fait foi.
    isStructureSourcedBdc() {
      return this.existingBdcSource === 'FINESS' || this.existingBdcSource === 'FINESSGEO'
    },
    step2Title() {
      return this.isStructureWithoutRpps
        ? 'Étape 2 : Responsable de la structure'
        : 'Étape 2 : Informations du praticien'
    },
    // Sans RPPS, le responsable porte seul le bénéficiaire structure envoyé à l'ASP.
    // Bloqué à la saisie plutôt que de laisser échouer l'export ASP des semaines plus
    // tard. Le code postal est validé à part (codePostalMissing), commun à tous les flux.
    responsableMissingFields() {
      if (!this.isStructureWithoutRpps) return []
      const missing = []
      if (!(this.nom || '').trim()) missing.push('Nom')
      if (!(this.prenom || '').trim()) missing.push('Prénom')
      const mail = (this.email || '').trim()
      if (!mail || mail.indexOf('@') < 1 || !mail.includes('.')) missing.push('Email')
      if (!this.telephoneAcceptable(this.telephone)) missing.push('Téléphone')
      return missing
    },
    // Code postal obligatoire hors pack V1 gratuit : il pilote le régime TVA côté serveur
    // (400 si absent). Auto-rempli depuis la fiche résident ou saisi manuellement.
    codePostalMissing() {
      if (this.isFreeV1) return false
      return !/^\d{5}$/.test((this.codePostal || '').trim())
    },
    // Référentiel ANS actif pour ce FINESS (flag on + lookup effectué).
    isAnsActive() {
      return !!this.ansMode && this.ansMode !== 'disabled'
    },
    isAnsResolved() {
      return this.ansMode === 'resolved'
    },
    isAnsAbsent() {
      return this.ansMode === 'absent'
    },
    // Tranche verrouillée : fournie par MSPList (mspFound) ou forcée par l'ANS.
    trancheLockedByLookup() {
      return this.mspFound || this.isAnsResolved
    },
    // Affichage FINESSGEO : visible pour tous sauf Prokov en mode mspFound
    // (Prokov a MSPList peuplee qui fournit FINESSGEO automatiquement).
    showFinessGeo() {
      if (this.commandePour !== 'STRUCTURE') return false
      const partnerCode = localStorage.getItem('partnerCode') || 'prokov'
      if (partnerCode === 'prokov' && this.mspFound && this.finessGeo) return false
      return true
    },
    // Validation stricte STRUCTURE : FINESS + FINESSGEO + raisonSociale + tranche.
    // V1 gratuit MSP : seuls FINESS et raisonSociale sont requis (workflow allégé).
    // V1 gratuit LIBERAL : pas de tranche (pas de tarification).
    structureComplete() {
      if (this.commandePour !== 'STRUCTURE') return true
      if (this.finessLoading || this.ansLoading) return false
      if (!this.finess || !/^(2[AB]\d{7}|\d{9})$/.test(this.finess)) return false
      if (!this.mspRaisonSociale || !this.mspRaisonSociale.trim()) return false
      if (this.isFreeV1Structure) return true
      // Référentiel ANS actif : structure absente => BDC structure interdit (libéral
      // possible) ; multi-sites non tranché => choix de site requis ; référentiel en
      // erreur => on bloque (fail-closed UI, cohérent avec le 503 serveur).
      if (this.isAnsAbsent) return false
      if (this.ansMode === 'ambiguous') return false
      if (this.ansMode === 'error') return false
      if (this.showFinessGeo && (!this.finessGeo || !/^(2[AB]\d{7}|\d{9})$/.test(this.finessGeo))) return false
      if (!this.isFreeV1 && !['A', 'B', 'C', 'D', 'E'].includes(this.tranche)) return false
      if (!this.mspFound && !this.mspChecked) return false
      // Occupation du site non tranchée, ou site déjà pris : le POST refuserait, on ne
      // laisse pas croire que la création va aboutir. 'taken' est une défense en
      // profondeur, le bloc de création étant déjà masqué quand existingBDC est posé.
      if (['loading', 'ambiguous', 'error', 'taken'].includes(this.siteOccupancy)) return false
      return true
    },
    // Liste des champs manquants pour tooltip explicite
    structureMissingFields() {
      if (this.commandePour !== 'STRUCTURE') return []
      const missing = []
      if (!this.finess) {
        missing.push('FINESS')
      } else if (!/^(2[AB]\d{7}|\d{9})$/.test(this.finess)) {
        missing.push('FINESS (9 chiffres ou 2A/2B)')
      }
      if (!this.mspRaisonSociale || !this.mspRaisonSociale.trim()) missing.push('Raison sociale')
      if (this.isFreeV1Structure) return missing
      if (this.isAnsAbsent) missing.push('Structure absente du référentiel ANS (basculer en libéral)')
      if (this.ansMode === 'ambiguous') missing.push('Choix du site (multi-sites)')
      if (this.ansMode === 'error') missing.push('Référentiel ANS indisponible (réessayer)')
      if (this.showFinessGeo && (!this.finessGeo || !/^(2[AB]\d{7}|\d{9})$/.test(this.finessGeo))) missing.push('FINESSGEO')
      if (!this.isFreeV1 && !['A', 'B', 'C', 'D', 'E'].includes(this.tranche)) missing.push('Tranche')
      if (this.siteOccupancy === 'loading') missing.push('Vérification du site en cours')
      if (this.siteOccupancy === 'ambiguous') {
        missing.push('Un bon de commande V2 de ce FINESS juridique n\'est rattaché à aucun site : le rattacher avant de continuer')
      }
      if (this.siteOccupancy === 'error') missing.push('Vérification du site impossible (réessayer)')
      if (this.siteOccupancy === 'taken') missing.push('Ce site a déjà un bon de commande V2')
      return missing
    }
  },
  watch: {
    commandePour(newVal) {
      // Reset champs STRUCTURE quand on bascule vers LIBERAL
      if (newVal === 'LIBERAL') {
        this.finess = ''
        this.finessGeo = ''
        this.effectif = null
        this.tranche = null
        this.mspChecked = false
        this.mspFound = false
        this.mspRaisonSociale = ''
        this.mspRaisonSocialeFromLookup = false
        this.mspCategorieFiness = null
        this.mspLibCategorieFiness = ''
        this.resetAnsState()
      } else {
        this.aidesPercues = false
        this.aidesDeMinimis = []
      }
      // Change de type = repart de zéro côté BDC repris.
      this.clearExistingBdcState()
      this.computePricingPreview()
    },
    codePostal(val) {
      if (val && val.length === 5) {
        this.computePricingPreview()
      }
    },
    tranche() {
      this.computePricingPreview()
    },
    finess(val) {
      // Lookup MSPList puis référentiel ANS des que 9 chiffres sont saisis.
      // Si moins, on reset l'etat.
      if (val && /^(2[AB]\d{7}|\d{9})$/.test(val)) {
        this.finessCompletionSuggestion = null
        this.lookupStructureFiness(val)
      } else {
        this._finessLookupToken++
        this.finessLoading = false
        this.mspChecked = false
        this.mspFound = false
        this.mspRaisonSociale = ''
        this.mspRaisonSocialeFromLookup = false
        this.finessGeo = ''
        this.effectif = null
        this.tranche = null
        this.mspCategorieFiness = null
        this.mspLibCategorieFiness = ''
        this.resetAnsState()
        // FINESS effacé/incomplet = pas d'étape 4 sur un ancien BDC, quelle que soit
        // l'identité structure qui l'avait ramené (FINESS saisi ou site résolu).
        if (this.isStructureSourcedBdc) this.clearExistingBdcState()
        // Saisie à 8 chiffres : proposer la clé de contrôle complétée par le serveur.
        this.finessCompletionSuggestion = null
        if (val && /^\d{8}$/.test(val)) this.fetchFinessCompletion(val)
      }
    }
  },
  methods: {
    /**
     * Prédicat d'acceptation du téléphone, calqué sur formatTelephone (jsonAspGenerator)
     * qui fait foi côté serveur : international +XX, international 00XX, ou national FR
     * normalisé en +33. Pré-contrôle UX, le serveur reste autoritaire ; il ne doit pas
     * être plus strict, sinon il bloquerait une saisie valide.
     */
    telephoneAcceptable(tel) {
      const cleaned = String(tel || '').trim().replace(/[\s().-]/g, '')
      if (!cleaned) return false
      if (cleaned.startsWith('+')) return /^\+\d{6,15}$/.test(cleaned)
      if (cleaned.startsWith('00')) return /^\+\d{6,15}$/.test('+' + cleaned.slice(2))
      if (!/^\d+$/.test(cleaned)) return false
      const normalized = cleaned.startsWith('0') ? '+33' + cleaned.slice(1) : '+33' + cleaned
      return /^\+\d{6,15}$/.test(normalized)
    },
    /**
     * Reagit a chaque saisie dans le champ RPPS.
     * Quand 11 chiffres valides, lance le chargement en cascade :
     * resident SEGUR -> licences -> fallback eeDb -> detection typePrestation -> pricing.
     */
    async onRppsInput() {
      this.rpps = this.rpps.replace(/\D/g, '')
      if (this.rpps.length > 11) this.rpps = this.rpps.slice(0, 11)

      if (/^\d{11}$/.test(this.rpps)) {
        this.rppsLoading = true
        this.rppsValid = null
        try {
          await this.fetchSegurResident(this.rpps)
          await this.fetchLicencesByRPPS(this.rpps)
          await this.fetchUserFromEeDb(this.rpps)
          await this.detectTypePrestation(this.rpps)
          this.computePricingPreview()
          this.rppsValid = true
        } catch {
          this.rppsValid = false
          this.showSnackbar('Erreur lors du chargement des donnees RPPS', 'error')
        } finally {
          this.rppsLoading = false
        }
      } else {
        this.rppsValid = this.rpps.length > 0 ? false : null
        // STRUCTURE + BDC repris via l'identité structure : ne pas effacer l'état
        // structure sur saisie RPPS partielle. Reset des seuls champs praticien.
        if (this.commandePour === 'STRUCTURE' && this.isStructureSourcedBdc) {
          this.licences = []
          this.selectedLicence = null
          this.manualLicenceId = ''
          this.manualLicenceValid = null
          return
        }
        this.resetForm()
      }
    },

    /**
     * Charge les donnees du resident SEGUR depuis le store Vuex.
     * Prefixe le RPPS avec "8" (convention SEGUR).
     */
    async fetchSegurResident(rpps) {
      const rppsWithPrefix = '8' + rpps
      try {
        await this.$store.dispatch('segur/getResidentSegurIDbyRPPS', rppsWithPrefix)
        const data = this.$store.getters['segur/getResidentID']
        if (data && data.data) {
          const r = data.data
          if (r.INFOS_PORTEUR_NomPatronym) {
            this.nom = r.INFOS_PORTEUR_NomPatronym
            this.prenom = r.INFOS_PORTEUR_PrenomUsuel || ''
            this.genre = r.INFOS_PORTEUR_CodeCivilite === 31 ? 'Monsieur' : 'Madame'
            this.telephone = r.phone || ''
            this.email = r.email || ''
            this.ville = r.city || ''
            this.codePostal = r.zip || ''
            this.adresse = r.address || ''
          }
        }
      } catch {
        // Pas de donnee resident, le fallback eeDb prendra le relais
      }
    },

    /**
     * Charge les licences MS4 associees au RPPS.
     * Auto-selectionne si une seule licence trouvee.
     */
    async fetchLicencesByRPPS(rpps) {
      try {
        const res = await axios.get(
          `${API_BASE}${server.urls.segur}/licences-by-rpps/${rpps}`,
          { headers: server.getHeader() }
        )
        this.licences = (res.data?.data || []).map(l => ({
          ...l,
          display: `${l.licenceID} — ${l.customer || ''}`
        }))
        if (this.licences.length === 1) {
          this.selectedLicence = this.licences[0].licenceID
        }
      } catch {
        this.licences = []
      }
    },

    /**
     * Fallback : charge les donnees utilisateur depuis eeDb si le resident SEGUR n'a pas ete trouve.
     * Ne remplit que les champs encore vides.
     */
    async fetchUserFromEeDb(rpps) {
      if (this.nom) return
      try {
        const res = await axios.get(
          `${API_BASE}${server.urls.segur}/user-by-rpps/${rpps}`,
          { headers: server.getHeader() }
        )
        const user = res.data?.data
        if (user) {
          this.nom = this.nom || user.familyname || ''
          this.prenom = this.prenom || user.givenname || ''
          this.telephone = this.telephone || user.phone || ''
          this.email = this.email || user.email || ''
          this.ville = this.ville || user.city || ''
          this.codePostal = this.codePostal || user.zip || ''
          this.adresse = this.adresse || user.address || ''
          if (!this.nom) {
            this.genre = user.sex === 1 ? 'Monsieur' : 'Madame'
          }
        }
      } catch {
        // Fallback silencieux
      }

      // 3eme fallback : annuaire RPPS ANS
      if (!this.nom) {
        await this.fetchFromRppsDb(rpps)
      }
    },

    /**
     * Dernier fallback : interroge l'annuaire RPPS de l'ANS.
     */
    async fetchFromRppsDb(rpps) {
      try {
        const res = await axios.get(
          `${API_BASE}/rpps/lookup/${rpps}`,
          { headers: server.getHeader() }
        )
        const data = res.data?.data
        if (data) {
          this.nom = data.nom || ''
          this.prenom = data.prenom || ''
          this.telephone = data.phone || ''
          this.email = data.email || ''
          this.ville = data.city || ''
          this.codePostal = data.zip || ''
          this.adresse = data.address || ''
          this.genre = (data.civilite === 'Mme' || data.civilite === 'Madame') ? 'Madame' : 'Monsieur'
        }
      } catch {
        // Annuaire RPPS indisponible
      }
    },

    /**
     * Detecte le type de prestation V2 ou V1V2 pour ce RPPS.
     * V2 = deja equipe en V1, V1V2 = primo-equipement.
     */
    async detectTypePrestation(rpps) {
      try {
        const res = await axios.get(
          `${API_BASE}${server.urls.segur}/v2/detect-type/${rpps}`,
          { headers: server.getHeader() }
        )
        this.typePrestation = res.data?.data?.typePrestation || 'V1V2'

        // Si un BDC V2 existe deja, reprendre le flow au bon endroit
        const existing = res.data?.data?.existingV2BDC
        if (existing) {
          this.existingBDC = existing
          this.numeroEditeur = existing.numeroEditeur
          this.orderFormId = existing.id || null
          this.existingBdcSource = 'RPPS'
          if (existing.hasPdf) {
            this.showSnackbar('Ce praticien a déjà un BDC V2 complet (PDF uploadé)', 'info')
          } else {
            this.showSnackbar('BDC V2 existant (n° ' + existing.numeroEditeur + '), PDF en attente', 'warning')
          }
        } else if (!this.isStructureSourcedBdc) {
          // Pas d'écrasement d'un BDC trouvé via l'identité structure par une détection
          // RPPS vide : le praticien n'est pas la clé d'un enrôlement structure.
          this.existingBDC = null
          this.numeroEditeur = null
          this.orderFormId = null
          this.existingBdcSource = null
        }
      } catch {
        this.typePrestation = 'V1V2'
        // Idem sur erreur réseau : ne pas effacer un BDC repris via la structure.
        if (!this.isStructureSourcedBdc) this.clearExistingBdcState()
      }
    },

    /**
     * Calcul local des montants (HT, TVA, TTC, avance 40%, solde 60%).
     * [SEGUR-SPEC] Grille de montants AF-MDV-LGC-Va2 Annexe 3.
     */
    computePricingPreview() {
      if (!this.typePrestation) {
        this.pricing = null
        return
      }

      const grid = MONTANTS[this.typePrestation]
      if (!grid) {
        this.pricing = null
        return
      }

      let montantHT
      if (this.commandePour === 'STRUCTURE') {
        // Sans tranche valide on ne peut PAS retomber sur le tarif liberal : ce serait faux.
        if (!this.tranche) {
          this.pricing = null
          return
        }
        montantHT = grid.STRUCTURE[this.tranche]
      } else {
        montantHT = grid.LIBERAL
      }

      if (!montantHT) {
        this.pricing = null
        return
      }

      const tauxTVA = getTauxTVA(this.codePostal)
      const montantTTC = Math.round(montantHT * (100 + tauxTVA)) / 100
      const avanceHT = round2(montantHT * 0.4)
      const soldeHT = round2(montantHT * 0.6)
      const avanceTTC = Math.round(avanceHT * (100 + tauxTVA)) / 100
      const soldeTTC = Math.round(soldeHT * (100 + tauxTVA)) / 100

      this.pricing = { montantHT, tauxTVA, montantTTC, avanceHT, soldeHT, avanceTTC, soldeTTC }
    },

    /**
     * Cree le bon de commande via POST /segur/createorderform.
     * Envoie l'objet au format attendu par le backend V2.
     */
    async createBDC() {
      this.creating = true
      this.backendError = null
      try {
        // Pour MSP V1 gratuit : pas de RPPS (gérant saisi sans RPPS), pas de licence.
        // Le résident gérant est créé/résolu côté backend par nom + prénom.
        const isFreeV1MSP = this.isFreeV1Structure
        // Clé absente et non pas vide : le résident doit être créé avec RPPS NULL, la
        // colonne portant un index UNIQUE que deux chaînes vides feraient collisionner.
        const rppsField = this.rppsRequired ? { RPPS: this.rpps } : {}
        const obj = {
          resident: {
            ...rppsField,
            INFOS_PORTEUR_PrenomUsuel: this.prenom,
            INFOS_PORTEUR_NomPatronym: this.nom,
            INFOS_PORTEUR_CodeCivilite: this.genre === 'Madame' ? 22 : 31,
            INFOS_PS2_CodeProfession: 10,
            phone: this.telephone,
            email: this.email,
            apps: localStorage.getItem('partnerApps') || 'MS4',
            city: this.ville,
            zip: this.codePostal,
            address: this.adresse
          },
          msp: this.commandePour === 'STRUCTURE'
            ? {
                FINESS: this.finess,
                FINESSGEO: this.finessGeo || '',
                effectifRef: this.effectif || null,
                raisonSociale: this.mspRaisonSociale || '',
                city: this.ville,
                zip: this.codePostal,
                address: this.adresse,
                partnerRef: (this.partnerRef || '').trim() || null,
              }
            : undefined,
          orderForm: {
            createdAt: this.dateSignature,
            licence: isFreeV1MSP ? null : (this.manualLicence ? this.manualLicenceId : this.selectedLicence),
            commandePour: this.commandePour,
            tranche: this.isFreeV1 ? null : this.tranche,
            aidesPercues: this.showAidesMinimis ? this.aidesPercues : false,
            aidesDeMinimis: this.showAidesMinimis ? this.aidesDeMinimis : []
          },
          isOutsideSegur: false,
          wave: this.isFreeV1 ? 'V1' : 'V2'
        }

        const res = await axios.post(
          `${API_BASE}${server.urls.segur}/createorderform`,
          { obj },
          { headers: server.getHeader() }
        )

        if (res.data?.data?.error) {
          // Erreur typee backend : errorCode est le champ machine canonique
          // (code conserve en repli pour compat).
          this.handleBackendError(res.data.data)
          return
        }

        const orderForm = res.data?.data?.orderForm || res.data?.data
        if (this.isFreeV1) {
          // V1 gratuit : pas d'étape 4. resetForm() préserve rpps/rppsValid/orderType par design,
          // on les vide explicitement pour repartir d'un formulaire vierge.
          this.showSnackbar('Pack V1 gratuit créé pour ce praticien (n° ' + (orderForm?.numeroEditeur || '?') + ')', 'success')
          this.resetForm()
          this.rpps = ''
          this.rppsValid = null
          this.orderType = 'withinSegur'
        } else {
          if (!orderForm?.id || !orderForm?.numeroEditeur) {
            this.backendError = {
              code: 'CREATE_RESPONSE_INVALID',
              msg: 'Le serveur a créé une réponse incomplète : numéro éditeur ou identifiant BDC manquant.'
            }
            return
          }
          this.numeroEditeur = orderForm?.numeroEditeur
          this.orderFormId = orderForm?.id || null
          // BDC fraîchement créé = brouillon OFF, jamais encore envoyé en signature.
          this.localAwaitingOrderFormId = null
          this.localSentForSignAt = null
          this.showSnackbar('BDC créé avec succès, numéro éditeur : ' + this.numeroEditeur, 'success')
          this.$nextTick(() => {
            const el = this.$refs.uploadStep?.$el
            if (el && typeof el.scrollIntoView === 'function') {
              el.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          })
        }
      } catch (err) {
        const data = err.response?.data?.data
        if (data) {
          this.handleBackendError(data)
        } else {
          const msg = err.response?.data?.message || err.message || 'Erreur lors de la creation du BDC'
          this.backendError = { code: 'ERROR', msg }
        }
      } finally {
        this.creating = false
      }
    },

    /**
     * Traite une erreur typee backend. STRUCTURE_ANS_AMBIGUOUS rouvre le choix de
     * site avec la liste renvoyee ; les autres codes alimentent l'alerte persistante.
     */
    handleBackendError(data) {
      const code = data.errorCode || data.code || 'ERROR'
      if (code === 'STRUCTURE_ANS_AMBIGUOUS' && Array.isArray(data.sites)) {
        this.ansMode = 'ambiguous'
        this.ansSites = data.sites
        this.ansDialog = true
        return
      }
      this.backendError = { code, msg: data.error }
    },

    /**
     * Télécharge le PDF du BDC brouillon à faire signer hors du logiciel.
     */
    async downloadDraftPdf() {
      if (!this.orderFormId) {
        this.showSnackbar('Aucun BDC brouillon a telecharger', 'warning')
        return
      }
      this.downloadingDraft = true
      try {
        const res = await axios.get(
          `${API_BASE}${server.urls.segur}/preview-bdc/${this.orderFormId}`,
          {
            headers: server.getHeader(),
            params: { signatureType: this.draftSignatureType },
            responseType: 'blob'
          }
        )
        const blob = new Blob([res.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `bdc-brouillon-${this.numeroEditeur}.pdf`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
        this.showSnackbar('BDC brouillon telecharge', 'success')
      } catch (err) {
        // Le backend retourne du JSON en cas d'erreur, donc on parse le blob.
        let msg = 'Erreur lors du telechargement du BDC brouillon'
        if (err.response?.data instanceof Blob) {
          try {
            const text = await err.response.data.text()
            const json = JSON.parse(text)
            msg = json?.data?.error || msg
          } catch (_e) { /* ignore parse error */ }
        }
        this.showSnackbar(msg, 'error')
      } finally {
        this.downloadingDraft = false
      }
    },

    /**
     * Upload du PDF signe en base64 via POST /segur/createorderformpdf.
     * Lit le fichier depuis le v-file-input, convertit en base64, puis envoie.
     */
    async sendPdf() {
      if (!this.pdfFile) {
        this.showSnackbar('Veuillez selectionner un fichier PDF', 'warning')
        return
      }
      if (this.pdfFile.size > 3 * 1024 * 1024) {
        this.showSnackbar('Le fichier ne doit pas depasser 3 Mo', 'warning')
        return
      }
      if (!this.numeroEditeur) {
        this.showSnackbar('Aucun numero editeur', 'warning')
        return
      }

      this.uploading = true
      try {
        const base64 = await this.fileToBase64(this.pdfFile)
        await axios.post(
          `${API_BASE}${server.urls.segur}/createorderformpdf`,
          { obj: { pdf: base64, numeroEditeur: this.numeroEditeur } },
          { headers: server.getHeader() }
        )
        this.showSnackbar('PDF envoye avec succes', 'success')
        this.pdfFile = null
        // BDC signé : masque l'étape 4 et reflète l'état backend dans l'alert récap.
        this.orderFormId = null
        this.numeroEditeur = null
        if (this.existingBDC) {
          this.existingBDC = { ...this.existingBDC, hasPdf: true, state: 'ENABLE' }
        }
      } catch (err) {
        const data = err.response?.data
        const msg = data?.data?.error || data?.error || data?.message || err.message || 'Erreur lors de l\'envoi du PDF'
        this.showSnackbar(msg, 'error')
      } finally {
        this.uploading = false
      }
    },

    // Envoie le BDC brouillon au médecin pour une première signature électronique à distance.
    async sendForSign() {
      if (!this.orderFormId) {
        this.showSnackbar('Aucun BDC à envoyer', 'warning')
        return
      }
      this.sendingForSign = true
      try {
        const res = await axios.post(
          `${API_BASE}${server.urls.segur}/v2/orderforms/${this.orderFormId}/send-for-sign`,
          {},
          { headers: server.getHeader() }
        )
        if (res.data?.success) {
          this.showSnackbar('BDC envoyé au médecin pour signature électronique', 'success')
          this.localAwaitingOrderFormId = this.orderFormId
          this.localSentForSignAt = res.data?.data?.sentForSignAt || new Date().toISOString()
          if (this.existingBDC) {
            this.existingBDC = { ...this.existingBDC, awaitingFirstSign: true, sentForSignAt: this.localSentForSignAt }
          }
        } else {
          this.showSnackbar(res.data?.data?.error || 'Envoi impossible', 'error')
        }
      } catch (err) {
        const data = err.response?.data
        this.showSnackbar(data?.data?.error || data?.error || err.message || 'Erreur lors de l\'envoi', 'error')
      } finally {
        this.sendingForSign = false
      }
    },

    // Annule l'envoi : le BDC repasse en brouillon (réutilisable en papier ou re-envoi).
    async cancelSendForSign() {
      if (!this.orderFormId) return
      this.sendingForSign = true
      try {
        const res = await axios.post(
          `${API_BASE}${server.urls.segur}/v2/orderforms/${this.orderFormId}/cancel-first-sign`,
          {},
          { headers: server.getHeader() }
        )
        if (res.data?.success) {
          this.showSnackbar('Envoi annulé : le BDC repasse en brouillon', 'success')
          this.localAwaitingOrderFormId = null
          this.localSentForSignAt = null
          if (this.existingBDC) {
            this.existingBDC = { ...this.existingBDC, awaitingFirstSign: false, sentForSignAt: null }
          }
        } else {
          this.showSnackbar(res.data?.data?.error || 'Annulation impossible', 'error')
        }
      } catch (err) {
        const data = err.response?.data
        this.showSnackbar(data?.data?.error || data?.error || err.message || 'Erreur lors de l\'annulation', 'error')
      } finally {
        this.sendingForSign = false
      }
    },

    formatSentForSign(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d.getTime())) return ''
      return d.toLocaleDateString('fr-FR') + ' à ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    },

    /**
     * Convertit un objet File en base64 (sans le prefixe data:...).
     */
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result.split(',')[1])
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },

    /**
     * Lookup MSPList par FINESS avec token de sequence pour eviter les race
     * conditions (saisie rapide = plusieurs requetes concurrentes, la derniere
     * reponse n'est pas forcement la derniere saisie). Le token est stocke sur
     * l'instance via created() pour ne pas etre reactif (pas de re-render inutile).
     */
    async lookupMspByFiness(finess, token = ++this._finessLookupToken) {
      this.finessLoading = true
      this.mspChecked = false
      try {
        const res = await axios.get(
          `${API_BASE}${server.urls.segur}/msp-by-finess/${finess}`,
          { headers: server.getHeader() }
        )
        if (token !== this._finessLookupToken) return
        const data = res.data?.data
        if (data && data.found && data.tranche) {
          this.mspFound = true
          this.mspRaisonSociale = data.raisonSociale || ''
          this.mspRaisonSocialeFromLookup = !!data.raisonSociale
          this.finessGeo = data.FINESSGEO || ''
          this.effectif = data.effectifRef || null
          this.tranche = data.tranche
          this.mspCategorieFiness = data.categorieFiness || null
          this.mspLibCategorieFiness = data.libCategorieFiness || ''
        } else {
          this.mspFound = false
          this.mspRaisonSociale = data?.raisonSociale || ''
          this.mspRaisonSocialeFromLookup = !!(data && data.raisonSociale)
          this.finessGeo = data?.FINESSGEO || ''
          this.effectif = data?.effectifRef || null
          this.tranche = null
          this.mspCategorieFiness = data?.categorieFiness || null
          this.mspLibCategorieFiness = data?.libCategorieFiness || ''
        }
        // Reprise BDC existant pour ce FINESS (cross-residentID).
        const existing = data?.existingV2BDC
        if (existing) {
          this.existingBDC = existing
          this.numeroEditeur = existing.numeroEditeur
          this.orderFormId = existing.id || null
          this.existingBdcSource = 'FINESS'
        } else {
          this.clearExistingBdcState()
        }
      } catch (e) {
        if (token !== this._finessLookupToken) return
        this.mspFound = false
        this.mspRaisonSociale = ''
        this.mspRaisonSocialeFromLookup = false
        this.finessGeo = ''
        this.effectif = null
        this.tranche = null
        this.mspCategorieFiness = null
        this.mspLibCategorieFiness = ''
        this.clearExistingBdcState()
      } finally {
        if (token === this._finessLookupToken) {
          this.mspChecked = true
          this.finessLoading = false
          this.computePricingPreview()
        }
      }
    },

    /**
     * Enchaine le lookup MSPList puis la resolution referentiel ANS pour un FINESS
     * de structure. Quand le referentiel est actif, l'ANS prime sur la tranche
     * declarative MSPList.
     */
    async lookupStructureFiness(finess) {
      // Un seul token pour toute la chaine MSPList -> ANS : si l'operateur change
      // le FINESS pendant un await, le token avance et la chaine obsolete s'arrete
      // (sinon un ANS tardif appliquerait la tranche/le prix du mauvais FINESS).
      const token = ++this._finessLookupToken
      await this.lookupMspByFiness(finess, token)
      if (token !== this._finessLookupToken) return
      await this.lookupAnsForStructure(finess, token)
    },

    /**
     * Récupère auprès du serveur le FINESS complété (clé de contrôle Luhn) d'une saisie
     * à 8 chiffres, pour le PROPOSER. Le serveur est l'unique autorité de complétion.
     * Même token que les autres lookups : une frappe ultérieure annule la proposition
     * obsolète (8 chiffres transitoires en route vers 9).
     */
    async fetchFinessCompletion(base8) {
      const token = ++this._finessLookupToken
      try {
        const res = await axios.get(
          `${API_BASE}${server.urls.segur}/ans-structure/${base8}`,
          { headers: server.getHeader() }
        )
        if (token !== this._finessLookupToken) return
        const data = res.data?.data
        if (data && data.finessCompleted && data.completedFiness) {
          this.finessCompletionSuggestion = data.completedFiness
        }
      } catch (e) {
        if (token !== this._finessLookupToken) return
        // Échec silencieux : pas de proposition, la saisie reste à compléter.
      }
    },

    /**
     * Adopte le FINESS complété sur clic de l'opérateur : le lookup 9 chiffres se
     * déclenche via le watcher. Jamais appliqué automatiquement.
     */
    adoptFinessCompletion() {
      if (!this.finessCompletionSuggestion) return
      this.finess = this.finessCompletionSuggestion
      this.finessCompletionSuggestion = null
    },

    /**
     * Resout le FINESS dans le referentiel ANS. Selon le mode :
     *  - resolved  : FINESSGEO + tranche forces (verrouilles), raison sociale si vide.
     *  - ambiguous : ouvre le choix de site (FINESSGEO/tranche effaces jusqu'au choix).
     *  - absent    : structure hors referentiel, BDC structure bloque (liberal possible).
     *  - disabled  : flag inactif, on garde la tranche declarative MSPList.
     * Token de sequence partage avec lookupMspByFiness pour ignorer les reponses
     * obsoletes en cas de saisie rapide.
     */
    async lookupAnsForStructure(finess, token = this._finessLookupToken) {
      this.resetAnsState()
      this.ansLoading = true
      try {
        // On passe le FINESSGEO saisi quand il est present : memes candidats
        // (geo d'abord) que la decision de creation, pour eviter une divergence
        // pre-check / POST.
        const geoQuery = this.finessGeo
          ? `?finessGeo=${encodeURIComponent(this.finessGeo)}`
          : ''
        const res = await axios.get(
          `${API_BASE}${server.urls.segur}/ans-structure/${finess}${geoQuery}`,
          { headers: server.getHeader() }
        )
        if (token !== this._finessLookupToken) return
        const data = res.data?.data
        if (!data || !data.enabled) {
          this.ansMode = 'disabled'
          return
        }
        this.ansMode = data.mode
        if (data.mode === 'resolved') {
          this.finessGeo = data.FINESSGEO || ''
          this.tranche = data.tranche
          if (!this.mspRaisonSociale && data.raisonSociale) {
            this.mspRaisonSociale = data.raisonSociale
          }
          if (this.isSiteIdentityEnabled) {
            await this.refreshExistingBdcForSite(this.finessGeo, token)
          }
        } else if (data.mode === 'ambiguous') {
          this.ansSites = data.sites || []
          this.finessGeo = ''
          this.tranche = null
          this.ansDialog = true
        } else if (data.mode === 'absent') {
          this.finessGeo = ''
          this.tranche = null
        }
      } catch (e) {
        if (token !== this._finessLookupToken) return
        // 503 = referentiel indisponible : fail-closed, on signale sans tarifer.
        this.ansMode = 'error'
        this.finessGeo = ''
        this.tranche = null
      } finally {
        if (token === this._finessLookupToken) {
          this.ansLoading = false
          this.computePricingPreview()
        }
      }
    },

    /**
     * Choix d'un site parmi les FINESSGEO d'un FINESS juridique multi-sites.
     * Fixe le FINESSGEO + la tranche du site, ferme le dialogue, passe en 'resolved'.
     */
    async selectAnsSite(site) {
      this.finessGeo = site.FINESSGEO
      this.tranche = site.tranche
      if (!this.mspRaisonSociale && site.raisonSociale) {
        this.mspRaisonSociale = site.raisonSociale
      }
      this.ansMode = 'resolved'
      this.ansSites = []
      this.ansDialog = false
      this.computePricingPreview()
      if (this.isSiteIdentityEnabled) {
        await this.refreshExistingBdcForSite(this.finessGeo)
      }
    },

    /**
     * Reprise d'un BDC existant pour le site retenu, une fois le FINESSGEO connu.
     * msp-by-finess répond au niveau du FINESS saisi, donc potentiellement juridique :
     * sur un juridique multi-sites il remonterait le BDC d'un site voisin. On réinterroge
     * par site dès que celui-ci est fixé (résolution ANS directe ou choix manuel).
     */
    async refreshExistingBdcForSite(finessGeo, token = this._finessLookupToken) {
      if (!finessGeo) {
        this.siteOccupancy = 'idle'
        return
      }
      this.siteOccupancy = 'loading'
      try {
        const res = await axios.get(
          `${API_BASE}${server.urls.segur}/existing-bdc-by-finessgeo/${finessGeo}`,
          { headers: server.getHeader() }
        )
        if (token !== this._finessLookupToken) return
        const data = res.data?.data
        if (data && data.ambiguous) {
          // Site indéterminable côté serveur : la création sera refusée en 409.
          this.siteOccupancy = 'ambiguous'
          this.clearExistingBdcState()
        } else if (data && data.occupied && data.existingV2BDC) {
          this.siteOccupancy = 'taken'
          this.existingBDC = data.existingV2BDC
          this.numeroEditeur = data.existingV2BDC.numeroEditeur
          this.orderFormId = data.existingV2BDC.id || null
          this.existingBdcSource = 'FINESSGEO'
        } else {
          // Site libre : purge une reprise héritée du lookup juridique.
          this.siteOccupancy = 'free'
          this.clearExistingBdcState()
        }
      } catch (e) {
        if (token !== this._finessLookupToken) return
        // Fail-closed : sans réponse fiable on ne déclare pas le site libre.
        this.siteOccupancy = 'error'
        this.clearExistingBdcState()
      }
    },

    resetAnsState() {
      this.ansMode = null
      this.ansSites = []
      this.ansDialog = false
      this.ansLoading = false
      // Le site n'est pas encore connu : ne pas garder l'occupation du FINESS précédent.
      this.siteOccupancy = 'idle'
    },

    /**
     * Verifie si une licence saisie manuellement existe en base.
     */
    async checkManualLicence() {
      if (!this.manualLicenceId || this.manualLicenceId.length < 3) {
        this.manualLicenceValid = null
        return
      }
      try {
        const res = await axios.get(
          `${API_BASE}${server.urls.segur}/check-licence/${this.manualLicenceId}`,
          { headers: server.getHeader() }
        )
        this.manualLicenceValid = !!(res.data?.data?.exists)
      } catch {
        this.manualLicenceValid = false
      }
    },

    formatEur(val) {
      if (val == null) return '—'
      return val.toFixed(2).replace('.', ',') + ' €'
    },
    copyNumeroEditeur() {
      if (this.numeroEditeur) {
        navigator.clipboard.writeText(this.numeroEditeur)
        this.showSnackbar('Numero editeur copie', 'info')
      }
    },

    showSnackbar(text, color) {
      this.snackbarText = text
      this.snackbarColor = color || 'success'
      this.snackbar = true
    },

    /**
     * Remet tous les champs du formulaire a leur valeur initiale.
     * Appele quand le RPPS change et n'est plus valide.
     */
    resetForm() {
      this.licences = []
      this.selectedLicence = null
      this.manualLicenceId = ''
      this.manualLicenceValid = null
      this.nom = ''
      this.prenom = ''
      this.telephone = ''
      this.email = ''
      this.adresse = ''
      this.codePostal = ''
      this.ville = ''
      this.finess = ''
      this.finessGeo = ''
      this.effectif = null
      this.tranche = null
      this.partnerRef = ''
      this.finessLoading = false
      this.mspChecked = false
      this.mspFound = false
      this.mspRaisonSociale = ''
      this.mspRaisonSocialeFromLookup = false
      this.mspCategorieFiness = null
      this.mspLibCategorieFiness = ''
      this.resetAnsState()
      this.backendError = null
      this.typePrestation = null
      this.pricing = null
      this.aidesPercues = false
      this.aidesDeMinimis = []
      this.draftSignatureType = 'manuscript'
      this.clearExistingBdcState()
    },

    // Reset de l'état BDC repris (FINESS ou RPPS).
    clearExistingBdcState() {
      this.existingBDC = null
      this.numeroEditeur = null
      this.orderFormId = null
      this.existingBdcSource = null
      this.localAwaitingOrderFormId = null
      this.localSentForSignAt = null
    }
  }
}
</script>

<style scoped>
.content {
  max-width: 900px;
  margin: 0 auto;
  padding: 16px;
}
.aides-minimis-panel {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  padding: 12px;
}
</style>
